import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { useNavigate } from "react-router-dom";
import { FaSyncAlt, FaSearch, FaClock, FaHashtag } from "react-icons/fa";

/**
 * TradeList Page (UPDATED)
 *
 * Rules:
 * 1) Removed "Click card to open" text.
 * 2) live-trades path: /live-trades/{entity_id}/{period}
 *    Example: /live-trades/bitcoin/30s
 * 3) live-trades opens ONLY when user clicks on a period tile (not card/outside).
 *
 * Endpoint: /admin/trade-list
 * Payload: { token }
 */

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function periodLabel(key) {
    const map = {
        period_30s: "30s",
        period_1m: "1m",
        period_3m: "3m",
        period_5m: "5m",
        period_10m: "10m",
        period_1h: "1h",
    };
    return map[key] || key;
}

function periodPathPart(key) {
    const map = {
        period_30s: "30s",
        period_1m: "1m",
        period_3m: "3m",
        period_5m: "5m",
        period_10m: "10m",
        period_1h: "1h",
    };
    return map[key] || key;
}

function PeriodTile({ label, count, value, onOpen }) {
    return (
        <button
            type="button"
            className="period-tile btn btn-light border rounded-3 p-2 w-100 text-start"
            onClick={onOpen}
            title={`Open ${label} live trades`}
        >
            <div className="d-flex align-items-center justify-content-between">
                <div className="text-muted small d-inline-flex align-items-center gap-2">
                    <FaClock /> {label}
                </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-1">
                <div className="small text-muted d-inline-flex align-items-center gap-1">
                    <FaHashtag /> {nfmt(count, 0)}
                </div>
                <div className="fw-semibold">{nfmt(value, 2)}</div>
            </div>
        </button>
    );
}

function SkeletonCard() {
    return (
        <div className="col-12 col-md-6 col-xl-4">
            <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                    <div className="placeholder-glow d-flex align-items-center gap-3">
                        <span className="placeholder rounded-circle" style={{ width: 44, height: 44 }} />
                        <div className="flex-grow-1">
                            <div className="placeholder col-6" />
                            <div className="placeholder col-4 mt-2" />
                        </div>
                    </div>

                    <div className="row g-2 mt-3">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div className="col-6" key={i}>
                                <div className="placeholder-glow">
                                    <span className="placeholder col-12" style={{ height: 44, display: "block" }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function TradeList() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");

    const fetchTrades = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/trade-list`,
                headers: { "Content-Type": "application/json" },
                data: { token: localStorage.getItem("token") || "" },
            });

            const data = res?.data;
            if (data?.error) {
                toast.error(typeof data?.error === "string" ? data.error : "Failed to load trades");
                setRows([]);
                return;
            }

            setRows(Array.isArray(data?.data) ? data.data : []);
        } catch (err) {
            console.log(err);
            toast.error("Failed to load trades");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTrades();
    }, [fetchTrades]);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return rows;
        return rows.filter((x) => {
            const id = safeText(x?.entity_id).toLowerCase();
            const name = safeText(x?.entity_name).toLowerCase();
            return id.includes(t) || name.includes(t);
        });
    }, [rows, q]);

    const openDetails = (entityId, period) => {
        if (!entityId || !period) return;
        navigate(`/live-trades/${encodeURIComponent(entityId)}/${encodeURIComponent(period)}`);
    };

    const periodKeys = ["period_30s", "period_1m", "period_3m", "period_5m", "period_10m", "period_1h"];

    // Calculate total count and amount for each entity
    const calculateEntityTotals = (trade) => {
        let totalCount = 0;
        let totalAmount = 0;

        periodKeys.forEach((periodKey) => {
            const periodData = trade?.[periodKey] || {};
            totalCount += Number(periodData.count) || 0;
            totalAmount += Number(periodData.value) || 0;
        });

        return { totalCount, totalAmount };
    };

    return (
        <PageTransition>
            <style>{`
        .btn-compact { padding: 4px 8px !important; line-height: 1.1 !important; }
        .coin-img { width: 44px; height: 44px; border-radius: 12px; object-fit: cover; }
        .period-tile { background: #fff; }
        .period-tile:hover { background: rgba(13,110,253,0.06); border-color: rgba(13,110,253,0.25) !important; }
        .total-stats { background: rgba(13,110,253,0.06); border-radius: 8px; padding: 4px 8px; min-width: 90px; }
        .total-stats .label { font-size: 0.7rem; color: #6c757d; }
        .total-stats .value { font-size: 0.85rem; font-weight: 600; }
      `}</style>

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h2 className="fw-bold mb-0">Trade List</h2>
                    <div className="text-muted">
                        Entities: <span className="fw-semibold">{nfmt(rows.length, 0)}</span>
                    </div>
                </div>

                <button
                    type="button"
                    className="btn btn-outline-primary btn-compact d-inline-flex align-items-center gap-2"
                    onClick={fetchTrades}
                    disabled={loading}
                    title="Refresh"
                >
                    <FaSyncAlt /> {loading ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            {/* Search */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body py-2">
                    <div className="row g-2 align-items-center">
                        <div className="col-12 col-lg-7">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text">
                                    <FaSearch />
                                </span>
                                <input
                                    className="form-control"
                                    placeholder="Search by coin name or entity_id..."
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-12 col-lg-5 d-flex justify-content-lg-end">
                            <div className="text-muted small">
                                Showing: <span className="fw-semibold">{nfmt(filtered.length, 0)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cards */}
            <div className="row g-3">
                {loading ? (
                    <>
                        <SkeletonCard />
                        <SkeletonCard />
                        <SkeletonCard />
                        <SkeletonCard />
                        <SkeletonCard />
                        <SkeletonCard />
                    </>
                ) : filtered.length === 0 ? (
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body text-center text-muted py-5">
                                <div className="fw-semibold">No trades found</div>
                                <div className="small">Try a different search.</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    filtered.map((x) => {
                        const entityId = safeText(x?.entity_id);
                        const name = safeText(x?.entity_name);
                        const img = x?.image;
                        const trade = x?.trade || {};

                        // Calculate totals for this entity
                        const { totalCount, totalAmount } = calculateEntityTotals(trade);

                        return (
                            <div className="col-12 col-md-6 col-xl-4" key={entityId}>
                                {/* NOT clickable card; only period tiles are clickable */}
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body">
                                        {/* Entity Header with Totals */}
                                        <div className="d-flex align-items-center gap-3 min-w-0 mb-3">
                                            {img ? (
                                                <img src={img} alt={name} className="coin-img" />
                                            ) : (
                                                <div
                                                    className="coin-img d-flex align-items-center justify-content-center"
                                                    style={{ background: "rgba(13,110,253,0.12)", color: "#0d6efd" }}
                                                >
                                                    <FaClock />
                                                </div>
                                            )}

                                            <div className="min-w-0 flex-grow-1">
                                                <div className="fw-bold text-truncate" title={name}>
                                                    {name}
                                                </div>
                                                <div className="text-muted small text-truncate" title={entityId}>
                                                    {entityId}
                                                </div>
                                            </div>

                                            {/* Total Count and Amount Display */}
                                            <div className="total-stats text-end">
                                                <div className="d-flex flex-column">
                                                    <div className="label">Total Trades: <span className="value">{nfmt(totalCount, 0)}</span></div>
                                                </div>
                                                <div className="d-flex flex-column mt-1">
                                                    <div className="label">Total Amount: <span className="value">{nfmt(totalAmount, 2)}</span></div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Period Tiles */}
                                        <div className="row g-2">
                                            {periodKeys.map((k) => {
                                                const period = periodPathPart(k);
                                                return (
                                                    <div className="col-6" key={k}>
                                                        <PeriodTile
                                                            label={periodLabel(k)}
                                                            count={trade?.[k]?.count || 0}
                                                            value={trade?.[k]?.value || 0}
                                                            onOpen={() => openDetails(entityId, period)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </PageTransition>
    );
}